import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BibleVerseLink, BibleVerseText } from '@/components/BibleVerseLink'
import { Separator } from '@/components/ui/separator'

/**
 * Demo page showing how to use the BibleVerseLink component
 * You can integrate this into your existing pages like Devotions, Testimonies, etc.
 */
export default function BibleVerseLinkDemo() {
  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Bible Verse Link Component</h1>
        <p className="text-muted-foreground">
          Click on any Bible verse to open it in your preferred Bible app
        </p>
      </div>

      {/* Inline Variant */}
      <Card>
        <CardHeader>
          <CardTitle>Inline Variant</CardTitle>
          <CardDescription>
            Use this for verse references within text content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            For God so loved the world, see{' '}
            <BibleVerseLink reference="John 3:16" variant="inline" />
          </p>

          <p>
            Read about love in{' '}
            <BibleVerseLink reference="1 Corinthians 13:4-7" variant="inline" />
          </p>

          <p>
            The Lord is my shepherd,{' '}
            <BibleVerseLink reference="Psalm 23:1-6" variant="inline" />
          </p>
        </CardContent>
      </Card>

      {/* Button Variant */}
      <Card>
        <CardHeader>
          <CardTitle>Button Variant</CardTitle>
          <CardDescription>
            Use this for standalone verse references as buttons
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <BibleVerseLink reference="John 3:16" variant="button" />
          <BibleVerseLink reference="Romans 8:28" variant="button" />
          <BibleVerseLink reference="Philippians 4:13" variant="button" />
          <BibleVerseLink reference="Jeremiah 29:11" variant="button" />
          <BibleVerseLink reference="Proverbs 3:5-6" variant="button" />
        </CardContent>
      </Card>

      {/* Badge Variant */}
      <Card>
        <CardHeader>
          <CardTitle>Badge Variant</CardTitle>
          <CardDescription>
            Use this for compact verse tags or labels
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <BibleVerseLink reference="Matthew 5:16" variant="badge" />
          <BibleVerseLink reference="Isaiah 40:31" variant="badge" />
          <BibleVerseLink reference="2 Timothy 3:16" variant="badge" />
          <BibleVerseLink reference="Hebrews 11:1" variant="badge" />
        </CardContent>
      </Card>

      <Separator />

      {/* Auto-parse text */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Parse Text (BibleVerseText)</CardTitle>
          <CardDescription>
            Automatically converts verse references in plain text to clickable links
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <BibleVerseText
              text="Today's sermon was on John 3:16 and Romans 8:28. We also discussed Philippians 4:13 and how it relates to Jeremiah 29:11. Don't forget to read Psalm 23:1-6 this week!"
            />
          </div>
        </CardContent>
      </Card>

      {/* Usage Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Examples</CardTitle>
          <CardDescription>
            How to integrate into your pages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">1. In Devotions Page:</h3>
            <div className="p-4 bg-muted rounded-lg text-sm">
              <code className="block whitespace-pre-wrap">
                {`<Card>
  <CardHeader>
    <CardTitle>Daily Devotion</CardTitle>
  </CardHeader>
  <CardContent>
    <p>
      Today's focus verse:{' '}
      <BibleVerseLink reference="Psalm 46:1" variant="inline" />
    </p>
    <p className="mt-4">
      God is our refuge and strength...
    </p>
  </CardContent>
</Card>`}
              </code>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">2. In Testimonies:</h3>
            <div className="p-4 bg-muted rounded-lg text-sm">
              <code className="block whitespace-pre-wrap">
                {`<Card>
  <CardHeader>
    <CardTitle>My Testimony</CardTitle>
  </CardHeader>
  <CardContent>
    <BibleVerseText
      text="When I was going through hard times, Romans 8:28
      and Jeremiah 29:11 really spoke to me..."
    />
  </CardContent>
</Card>`}
              </code>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">3. In Prayer Requests:</h3>
            <div className="p-4 bg-muted rounded-lg text-sm">
              <code className="block whitespace-pre-wrap">
                {`<Card>
  <CardHeader>
    <CardTitle>Prayer Request</CardTitle>
  </CardHeader>
  <CardContent>
    <p>Please pray for healing...</p>
    <div className="mt-4 flex gap-2">
      <span className="text-sm text-muted-foreground">
        Standing on:
      </span>
      <BibleVerseLink
        reference="James 5:15"
        variant="badge"
      />
    </div>
  </CardContent>
</Card>`}
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supported Bible Apps */}
      <Card>
        <CardHeader>
          <CardTitle>Supported Bible Apps</CardTitle>
          <CardDescription>
            The component links to these popular Bible resources
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>YouVersion Bible App</strong> - Opens in mobile app if installed, otherwise opens web version
            </li>
            <li>
              <strong>Bible Gateway</strong> - Popular web-based Bible with multiple translations
            </li>
            <li>
              <strong>Blue Letter Bible</strong> - In-depth Bible study tools and commentaries
            </li>
            <li>
              <strong>ESV.org</strong> - English Standard Version official website
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
